import {
    useState,
    useEffect,
    useContext,
    FC,
    useCallback,
    useRef,
    ChangeEvent,
    MouseEventHandler,
    ReactNode,
} from 'react';
import serverVars from 'server-vars';
import classNames from 'classnames';
import { FormattedMessage, MessageDescriptor } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { graphql, useFragment, useRelayEnvironment, useMutation } from 'react-relay';
import { isEditing, showPreFilledAddressNotice } from '../reducers/checkoutShippingReducer';
import { getEmailOnlyErrors, getAddressErrors } from '../helpers/checkoutForms';
import ctaStyles from '../components/shared/styles/checkoutCTAButton.scss';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import {
    trackFormInteraction,
    trackGuestEmailError,
    trackGuestEmailRegistration,
} from '../helpers/checkoutTracking';
import {
    openGuestCheckoutEmailExistsLoginModal,
    openGuestCheckoutEmailLinkSentModal,
} from '../actions/checkoutAuthActions';
import { setTriggeredErrors } from '../actions/checkoutUIActions';
import { CHECKOUT_REGISTRATION_TRIGGER } from '../constants/checkoutConstants';
import { ControlledAddressForm } from 'dibs-address-form';
import { CheckoutShippingTaxWarning } from '../components/shared/CheckoutShippingTaxWarning';
import Email from '../components/forms/fields/Email';
import { CheckoutRegistrationMarketingOptIn } from '../components/forms/CheckoutRegistrationMarketingOptIn';
import { useAppDispatch, useCheckoutSelector } from '../reducers/root';
import CheckoutUpdateAction from '../components/shared/actions/CheckoutUpdateAction';
import CheckoutSaveAndContinueAction from '../components/shared/actions/CheckoutSaveAndContinueAction';
import { CheckoutRegistrationTerms } from '../components/forms/CheckoutRegistrationTerms';
import { CheckoutEmailOnlyShippingForm_viewer$key } from './__generated__/CheckoutEmailOnlyShippingForm_viewer.graphql';
import {
    CheckoutEmailOnlyShippingFormRegisterMutation,
    CheckoutEmailOnlyShippingFormRegisterMutation$data,
} from './__generated__/CheckoutEmailOnlyShippingFormRegisterMutation.graphql';

import styles from './styles/CheckoutEmailOnlyShippingForm.scss';
import dibsCss from 'dibs-css';
import { logCheckoutError } from '../helpers/logCheckoutError';
import { executeCaptcha } from 'dibs-recaptcha/src/recaptcha';

type CheckoutBlurEventHandler = (arg: { field: string; value: string }) => void;
type CheckoutChangeEventHandler = (arg: {
    event: ChangeEvent;
    field: string;
    value: string;
}) => void;

type CheckoutValidationResult = {
    isValid: boolean;
    isEmailValid: boolean;
    errors: Record<string, ReactNode | MessageDescriptor>;
};

type Props = {
    viewer: CheckoutEmailOnlyShippingForm_viewer$key;
    wrapperClass: string;
    onChange: CheckoutChangeEventHandler;
    onBlur: CheckoutBlurEventHandler;
    onSubmit: (arg: CheckoutValidationResult) => void;
};

const CheckoutEmailOnlyShippingForm: FC<Props> = props => {
    const { viewer: viewerRef, wrapperClass, onChange, onBlur } = props;
    const environment = useRelayEnvironment();
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const dispatch = useAppDispatch();
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [commitEmailMutation] =
        useMutation<CheckoutEmailOnlyShippingFormRegisterMutation>(graphql`
            mutation CheckoutEmailOnlyShippingFormRegisterMutation(
                $input: UserRegisterEmailOnlyInput!
            ) {
                userRegisterEmailOnly(input: $input) {
                    userId
                    errorCode
                    email
                    userExistsMetadata {
                        ...checkoutAuthActions_userExistsMetadata
                    }
                }
            }
        `);

    const {
        isEditingShipping,
        newShippingForm,
        editShippingForm,
        registrationForm,
        wasEmailPrefilled,
        showTaxWarning,
        showNotice,
        isSavingAddress,
        errorCode,
        showAddressFormErrors,
        isSelectedAddressValid,
        offerCurrency,
    } = useCheckoutSelector(({ shipping, ui, transaction }) => ({
        isEditingShipping: isEditing(shipping),
        newShippingForm: shipping.newShippingForm,
        editShippingForm: shipping.editShippingForm,
        registrationForm: shipping.registrationForm,
        wasEmailPrefilled: shipping.wasEmailPrefilled,
        showTaxWarning: shipping.showTaxWarning,
        showNotice: showPreFilledAddressNotice(shipping),
        isSavingAddress: ui.isSavingAddress,
        errorCode: ui.errorCode,
        showAddressFormErrors: shipping.showAddressFormErrors,
        isSelectedAddressValid: shipping.isSelectedAddressValid,
        offerCurrency: transaction.offerCurrency,
    }));
    const inputRef = useRef<HTMLInputElement>();
    const didCheckPrefilledEmail = useRef<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [wasSubmitted, setWasSubmitted] = useState(false);
    const [isEmailBlurred, setIsEmailBlurred] = useState(false);
    const [emailNetworkError, setEmailNetworkError] = useState(false);
    const [userInfo, setUserInfo] = useState<NonNullable<
        CheckoutEmailOnlyShippingFormRegisterMutation$data['userRegisterEmailOnly']
    > | null>(null);

    const viewer = useFragment(
        graphql`
            fragment CheckoutEmailOnlyShippingForm_viewer on Viewer {
                ...CheckoutRegistrationForm_viewer
                ...ControlledAddressForm_viewer
                ...CheckoutRegistrationMarketingOptIn_viewer
                __typename
                enableEmailOnlyCheckoutModalOnBlur: featureFlag(
                    feature: "emailOnlyCheckoutModalOnBlur"
                )
                enableCheckoutAddressAutocomplete: featureFlag(
                    feature: "checkoutAddressAutocomplete"
                )
            }
        `,
        viewerRef
    );

    const onEmailExistsLogin = useCallback<MouseEventHandler>(
        event => {
            event.preventDefault();
            trackFormInteraction();
            const { email, userExistsMetadata } = userInfo || {};
            openGuestCheckoutEmailExistsLoginModal({
                email,
                userExistsMetadata,
                dispatch,
            });
        },
        [dispatch, userInfo]
    );

    const onEmailOnlyExistsLogin = useCallback<MouseEventHandler>(
        event => {
            event.preventDefault();
            trackFormInteraction();
            openGuestCheckoutEmailLinkSentModal({ email: userInfo?.email, dispatch, environment });
        },
        [dispatch, userInfo, environment]
    );

    const validation = (): CheckoutValidationResult => {
        const addressForm = isEditingShipping ? editShippingForm : newShippingForm;
        const { isValid: isValidAddress, errors: addressErrors } = getAddressErrors(addressForm);
        //TODO ENGAGE-1259 once checkout disabler is removed we are no longer registering users inline

        const registrationFormValidation = getEmailOnlyErrors(registrationForm);
        const isValidRegistrationForm = registrationFormValidation.isValid;

        let emailError: ReactNode = registrationFormValidation.email;
        if (
            !registrationFormValidation.errors.email &&
            userInfo?.email === registrationForm.email &&
            userInfo?.errorCode === 'EMAIL_ONLY_ACCOUNT_EXISTS'
        ) {
            emailError = (
                <FormattedMessage
                    id="checkout.CheckoutEmailOnlyShippingForm.emailOnlyAccountExists"
                    defaultMessage="We require verification for you to proceed. <a>Verify your email address</a>"
                    values={{
                        a: content => <Link onClick={onEmailOnlyExistsLogin}>{content}</Link>,
                    }}
                />
            );
        } else if (
            !registrationFormValidation.errors.email &&
            userInfo?.email === registrationForm.email &&
            userInfo?.errorCode === 'FULL_ACCOUNT_EXISTS'
        ) {
            emailError = (
                <FormattedMessage
                    id="checkout.CheckoutEmailOnlyShippingForm.fullAccountExists"
                    defaultMessage="We noticed you have an account. <a>Log in to complete your purchase</a>"
                    values={{
                        a: content => <Link onClick={onEmailExistsLogin}>{content}</Link>,
                    }}
                />
            );
        } else if (emailNetworkError) {
            emailError = (
                <FormattedMessage
                    id="checkout.CheckoutEmailOnlyShippingForm.emailNetworkError"
                    defaultMessage="There was an error verifying your email address"
                />
            );
        }

        return {
            isValid: isValidAddress && isValidRegistrationForm && !emailError,
            isEmailValid: registrationFormValidation.isValid,
            errors: {
                ...addressErrors,
                email: registrationFormValidation.errors.email || emailError,
            },
        };
    };
    const { errors = {}, isEmailValid } = validation();

    const onEmailBlur = useCallback<CheckoutBlurEventHandler>(
        async ({ field, value }) => {
            setIsEmailLoading(true);
            setIsEmailBlurred(true);
            setEmailNetworkError(false);
            onBlur({ field, value });
            if (isEmailValid && (!userInfo || userInfo.email !== value)) {
                setWasSubmitted(false);
                const captchaResults = await executeCaptcha({
                    action: 'checkoutRegister',
                    featureFlag: 'registerCaptcha',
                    relayEnvironment: environment,
                });
                await new Promise<void>(resolve => {
                    commitEmailMutation({
                        variables: {
                            input: {
                                email: value,
                                authHash: btoa((value || '').split('@')[0]),
                                cookieDomain: serverVars.get('cookieDomain'),
                                captchaToken: captchaResults?.captchaToken,
                                captchaKey: captchaResults?.captchaKey,
                                registrationTrigger: CHECKOUT_REGISTRATION_TRIGGER,
                                currency: offerCurrency,
                            },
                        },
                        onCompleted(result) {
                            if (result.userRegisterEmailOnly) {
                                const mutationErrorCode = result.userRegisterEmailOnly?.errorCode;
                                setUserInfo(result.userRegisterEmailOnly);
                                const { email, userExistsMetadata } = result.userRegisterEmailOnly;
                                if (mutationErrorCode) {
                                    if (mutationErrorCode === 'EMAIL_ONLY_ACCOUNT_EXISTS') {
                                        trackGuestEmailError('verify your email error display');
                                        if (viewer.enableEmailOnlyCheckoutModalOnBlur) {
                                            openGuestCheckoutEmailLinkSentModal({
                                                email,
                                                dispatch,
                                                environment,
                                            });
                                        }
                                    } else if (mutationErrorCode === 'FULL_ACCOUNT_EXISTS') {
                                        trackGuestEmailError(
                                            'we noticed you have an account display'
                                        );
                                        if (viewer.enableEmailOnlyCheckoutModalOnBlur) {
                                            openGuestCheckoutEmailExistsLoginModal({
                                                email,
                                                userExistsMetadata,
                                                dispatch,
                                            });
                                        }
                                    }
                                    dispatch(setTriggeredErrors([mutationErrorCode]));
                                } else {
                                    trackGuestEmailRegistration();
                                }
                            } else {
                                setUserInfo(null);
                                setEmailNetworkError(true);
                            }
                            resolve();
                        },
                        onError(e) {
                            setUserInfo(null);
                            setEmailNetworkError(true);
                            logCheckoutError(e, 'guestemail');
                            dispatch(setTriggeredErrors(['guest-email-network']));
                            resolve();
                        },
                    });
                });
            }
            setIsEmailLoading(false);
        },
        [
            onBlur,
            isEmailValid,
            userInfo,
            environment,
            commitEmailMutation,
            dispatch,
            viewer.enableEmailOnlyCheckoutModalOnBlur,
            offerCurrency,
        ]
    );

    useEffect(() => {
        if (wasEmailPrefilled && !didCheckPrefilledEmail.current) {
            didCheckPrefilledEmail.current = true;
            onEmailBlur({ field: 'email', value: registrationForm.email });
        }
    }, [onEmailBlur, wasEmailPrefilled, registrationForm.email]);

    const { enableCheckoutAddressAutocomplete } = viewer || {};

    const submitClasses = classNames(ctaStyles.ssoButtonNew, wrapperClass);

    const onSubmit = (): void => {
        setIsSubmitting(true);
        setWasSubmitted(true);
        setIsEmailBlurred(true);
        const result = validation();
        const value = inputRef.current?.value;
        if (isEmailValid && !isEmailLoading && value) {
            // just in case email field never blurred
            if (!userInfo || userInfo.email !== value) {
                onEmailBlur({ field: 'email', value });
                setIsSubmitting(false);
                return;
            }
            // if email error is displayed and user clicks continue, show modal from error
            if (userInfo && userInfo.email === value && userInfo.errorCode) {
                if (userInfo.errorCode === 'EMAIL_ONLY_ACCOUNT_EXISTS') {
                    openGuestCheckoutEmailLinkSentModal({
                        email: userInfo?.email,
                        dispatch,
                        environment,
                    });
                } else if (userInfo.errorCode === 'FULL_ACCOUNT_EXISTS') {
                    const { email, userExistsMetadata } = userInfo;
                    openGuestCheckoutEmailExistsLoginModal({
                        email,
                        userExistsMetadata,
                        dispatch,
                    });
                }
                setIsSubmitting(false);
                return;
            }
        }

        props.onSubmit(result);
        setIsSubmitting(false);
    };

    return (
        <div
            className={classNames(isMobile ? styles.mobileFormWrapper : styles.shippingFormWrapper)}
        >
            {showNotice && (
                <p className={styles.notice}>
                    <FormattedMessage
                        id="checkout.CheckoutEmailOnlyShippingForm.addressNotice"
                        defaultMessage="The city, state, country and postal code below were provided when your custom shipping quote was requested. Please note that if you change any of that information your custom quote will no longer apply."
                    />
                </p>
            )}
            <Email
                autoFocus
                value={registrationForm.email}
                // @ts-expect-error "error" proptypes inferred from Email is incorrect should allow ReactNode
                error={isEmailLoading ? null : errors.email}
                isBlurred={isEmailBlurred}
                onChange={onChange}
                onBlur={onEmailBlur}
                disabled={isSavingAddress}
                environment={environment}
                rightDecorator={isEmailLoading ? <Spinner /> : undefined}
                rightDecoratorClass={isEmailLoading ? dibsCss.wLarge : undefined}
                showIcons={isEmailLoading ? false : undefined}
                inputRef={(node: HTMLInputElement) => {
                    inputRef.current = node;
                }}
            />
            {!errors.email && <CheckoutRegistrationMarketingOptIn viewer={viewer} />}
            <ControlledAddressForm
                viewer={viewer}
                address={isEditingShipping ? editShippingForm : newShippingForm}
                theme={{ submit: submitClasses, labelClass: styles.label }}
                submitLabel={
                    isEditingShipping ? <CheckoutUpdateAction /> : <CheckoutSaveAndContinueAction />
                }
                onUpdate={onChange}
                onBlur={onBlur}
                onSubmit={onSubmit}
                isSaving={isSavingAddress}
                isEditing={isEditingShipping && isSelectedAddressValid}
                disableForm={!!errorCode}
                disableSubmit={!!errorCode || isEmailLoading || isSubmitting}
                hasRegistrationFormErrors={wasSubmitted && !!errors.email}
                showAllErrors={showAddressFormErrors || wasSubmitted}
                isMobile={isMobile}
                enableAddressAutocomplete={enableCheckoutAddressAutocomplete}
            />
            {isEditingShipping && showTaxWarning && <CheckoutShippingTaxWarning />}
            <div className={dibsCss.mtSmall}>
                <CheckoutRegistrationTerms />
            </div>
        </div>
    );
};

export default CheckoutEmailOnlyShippingForm;
